/**
 * Utility functions for generating and handling order receipts
 */

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  awaiting_payment: 'Awaiting Payment',
};

const getStatusLabel = (status) => {
  return STATUS_LABELS[status?.toLowerCase()] || status || 'Pending';
};

const defaultFormatCurrency = (amount) => {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  if (!dateString || isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const generateReceiptHTML = (order, merchant, formatCurrency) => {
  const fmt = typeof formatCurrency === 'function' ? formatCurrency : defaultFormatCurrency;
  const merchantName = merchant?.name || order?.merchant?.name || 'Restaurant';
  const merchantAddress = merchant?.address || order?.merchant?.address || '';
  const merchantPhone = merchant?.phone || order?.merchant?.phone || '';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Receipt - ${order.order_number}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            background: #f5f5f5;
          }

          .receipt {
            max-width: 600px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }

          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #333;
          }

          .header h1 {
            font-size: 28px;
            color: #333;
            margin-bottom: 10px;
          }

          .header p {
            color: #666;
            font-size: 14px;
            line-height: 1.6;
          }

          .order-info {
            margin-bottom: 30px;
          }

          .order-info h2 {
            font-size: 18px;
            color: #333;
            margin-bottom: 15px;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            color: #555;
            font-size: 14px;
          }

          .info-row strong {
            color: #333;
          }

          .divider {
            border-top: 1px dashed #ddd;
            margin: 20px 0;
          }

          .items-section {
            margin-bottom: 30px;
          }

          .items-section h3 {
            font-size: 16px;
            color: #333;
            margin-bottom: 15px;
          }

          .item {
            margin-bottom: 15px;
            padding: 12px;
            background: #f9f9f9;
            border-radius: 6px;
          }

          .item-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .item-name {
            font-weight: 600;
            color: #333;
            font-size: 15px;
          }

          .item-price {
            font-weight: 600;
            color: #333;
            font-size: 15px;
          }

          .item-details {
            display: flex;
            justify-content: space-between;
            color: #666;
            font-size: 13px;
          }

          .item-options {
            margin-top: 6px;
            padding-left: 12px;
            font-size: 13px;
            color: #777;
          }

          .item-option {
            margin-top: 2px;
          }

          .total-section {
            background: #f9f9f9;
            padding: 20px;
            border-radius: 6px;
            margin-bottom: 20px;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 15px;
            color: #555;
          }

          .total-row.subtotal,
          .total-row.tax,
          .total-row.service {
            font-size: 14px;
          }

          .total-row.grand-total {
            border-top: 2px solid #333;
            margin-top: 10px;
            padding-top: 12px;
            font-size: 18px;
            font-weight: 700;
            color: #333;
          }

          .payment-section {
            margin-bottom: 30px;
            padding: 15px;
            background: #e8f5e9;
            border-radius: 6px;
            border-left: 4px solid #4caf50;
          }

          .payment-section h3 {
            font-size: 16px;
            color: #2e7d32;
            margin-bottom: 10px;
          }

          .payment-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 14px;
            color: #333;
          }

          .footer {
            text-align: center;
            color: #666;
            font-size: 13px;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px dashed #ddd;
          }

          .footer p {
            margin: 5px 0;
          }

          .status-badge {
            display: inline-block;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .status-pending {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }

          .status-confirmed {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
          }

          .status-preparing {
            background: rgba(139, 92, 246, 0.1);
            color: #8b5cf6;
          }

          .status-ready {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          .status-delivered {
            background: rgba(6, 182, 212, 0.1);
            color: #06b6d4;
          }

          .status-completed {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          .status-cancelled {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }

          .status-awaiting_payment {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }

          @media print {
            body {
              background: white;
              padding: 0;
            }

            .receipt {
              box-shadow: none;
              padding: 20px;
            }

            @page {
              margin: 1cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>${merchantName}</h1>
            ${merchantAddress ? `<p>${merchantAddress}</p>` : ''}
            ${merchantPhone ? `<p>Phone: ${merchantPhone}</p>` : ''}
          </div>

          <div class="order-info">
            <h2>Order Details</h2>
            <div class="info-row">
              <span>Order Number:</span>
              <strong>${order.order_number}</strong>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${formatDateTime(order.created_at)}</span>
            </div>
            ${order.customer_name ? `
            <div class="info-row">
              <span>Customer:</span>
              <span>${order.customer_name}</span>
            </div>
            ` : ''}
            ${order.table?.name ? `
            <div class="info-row">
              <span>Table:</span>
              <span>${order.table.name}</span>
            </div>
            ` : ''}
            <div class="info-row">
              <span>Status:</span>
              <span class="status-badge status-${order.status?.toLowerCase() || 'pending'}">${getStatusLabel(order.status)}</span>
            </div>
          </div>

          <div class="divider"></div>

          <div class="items-section">
            <h3>Order Items</h3>
            ${order.items?.map(item => `
              <div class="item">
                <div class="item-header">
                  <span class="item-name">${item.menu_item?.name || item.name || 'Item'}</span>
                  <span class="item-price">${fmt(item.total)}</span>
                </div>
                <div class="item-details">
                  <span>Quantity: ${item.quantity}</span>
                  <span>@ ${fmt(item.price)} each</span>
                </div>
                ${item.options && item.options.length > 0 ? `
                  <div class="item-options">
                    ${item.options.map(opt => `
                      <div class="item-option">+ ${opt.name || opt.option_name} ${opt.price_addition > 0 ? `(+${fmt(opt.price_addition)})` : ''}</div>
                    `).join('')}
                  </div>
                ` : ''}
                ${item.notes ? `
                  <div class="item-options">
                    <div class="item-option">Note: ${item.notes}</div>
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>

          <div class="divider"></div>

          <div class="total-section">
            <div class="total-row subtotal">
              <span>Subtotal:</span>
              <span>${fmt(order.subtotal)}</span>
            </div>
            ${order.tax > 0 ? `
            <div class="total-row tax">
              <span>Tax:</span>
              <span>${fmt(order.tax)}</span>
            </div>
            ` : ''}
            ${order.service_charge > 0 ? `
            <div class="total-row service">
              <span>Service Charge:</span>
              <span>${fmt(order.service_charge)}</span>
            </div>
            ` : ''}
            ${order.discount_details && order.discount_details.length > 0
              ? order.discount_details.map(disc => `
            <div class="total-row discount">
              <span>${disc.name}${disc.is_auto ? ' (Auto)' : ''}:</span>
              <span>-${fmt(Number(disc.amount))}</span>
            </div>
            `).join('')
              : order.discount > 0 ? `
            <div class="total-row discount">
              <span>Discount:</span>
              <span>-${fmt(order.discount)}</span>
            </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>Total:</span>
              <span>${fmt(order.total)}</span>
            </div>
          </div>

          ${order.payment_status === 'COMPLETED' ? `
          <div class="payment-section">
            <h3><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style="vertical-align:-2px;margin-right:4px"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" /></svg>Payment Confirmed</h3>
            <div class="payment-row">
              <span>Payment Status:</span>
              <strong>PAID</strong>
            </div>
            <div class="payment-row">
              <span>Amount Paid:</span>
              <strong>${fmt(order.total)}</strong>
            </div>
          </div>
          ` : ''}

          ${order.notes ? `
          <div class="divider"></div>
          <div class="order-info">
            <h3>Order Notes</h3>
            <p style="color: #666; font-size: 14px; margin-top: 10px;">${order.notes}</p>
          </div>
          ` : ''}

          <div class="footer">
            <p><strong>Thank you for your order!</strong></p>
            <p>This is your official receipt</p>
            <p>Generated on ${formatDateTime(new Date().toISOString())}</p>
            <p style="margin-top: 16px; font-size: 11px; color: #999;">Powered by <strong style="color: #667eea;">MerchQry</strong></p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Print receipt
 */
export const printReceipt = (order, merchant, formatCurrency) => {
  const receiptHTML = generateReceiptHTML(order, merchant, formatCurrency);
  const printWindow = window.open('', '_blank', 'width=800,height=600');

  if (printWindow) {
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
    };
  } else {
    alert('Please allow pop-ups to print the receipt');
  }
};

/**
 * Save receipt as HTML file
 */
export const saveReceipt = (order, merchant, formatCurrency) => {
  const receiptHTML = generateReceiptHTML(order, merchant, formatCurrency);
  const blob = new Blob([receiptHTML], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `receipt-${order.order_number}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const receiptUtils = {
  generateReceiptHTML,
  printReceipt,
  saveReceipt
};

export default receiptUtils;

